import { AriaDescriber, FocusMonitor } from '@angular/cdk/a11y';
import { Directionality } from '@angular/cdk/bidi';
import { Overlay } from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import { ScrollDispatcher } from '@angular/cdk/scrolling';
import { DOCUMENT } from '@angular/common';
import { Directive, ElementRef, HostListener, Inject, Input, NgZone, Optional, ViewContainerRef } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatListItem } from '@angular/material/list';
import {
	MAT_TOOLTIP_DEFAULT_OPTIONS,
	MAT_TOOLTIP_SCROLL_STRATEGY,
	MatTooltip,
	MatTooltipDefaultOptions,
	TooltipPosition
} from '@angular/material/tooltip';

/*
 * ```shOverflowTooltip``` is a directive that displays a tooltip when the text overflows on a provided element
 *
 * This directive accepts the tooltip text as a string and the element as a HTMLElement, MatButton or MatListItem.
 * It will display that string as a tooltip if that string is truncated or cut off on the provided element by overflow styling.
 *
 * **Usage**
 *
 * In the example below we bind the ```[shOverflowTooltip]``` input property to the text string
 * and we bind the ```[shTooltipElement]```input property to a span element that displays the string.
 * We are assuming that any overflow is hidden by styling and will display the text input when scrollWidth exceeds
 * offsetWidth on the given element.
 *
 * ```html
 *  <div class="sh-message" [shOverflowTooltip]="message" [shTooltipElement]="messageSpan">
 *		<span #messageSpan id="sh-message-span">
 *           {{ message }}
 *      </span>
 *  </div>
 * ```
 *
 */

@Directive({
	selector: '[shOverflowTooltip]',
	standalone: true
})
export class TooltipOverflowTextDirective extends MatTooltip {
	@Input('shOverflowTooltip') public tooltipText!: string;
	@Input() public shTooltipElement?: HTMLElement | MatButton | MatListItem | MatOption;
	@Input() public shTooltipPosition?: TooltipPosition;
	@Input() public shTooltipDisabled?: boolean;
	@Input() public vertical = false;

	constructor(
		overlay: Overlay,
		elementRef: ElementRef<HTMLElement>,
		scrollDispatcher: ScrollDispatcher,
		viewContainerRef: ViewContainerRef,
		ngZone: NgZone,
		platform: Platform,
		ariaDescriber: AriaDescriber,
		focusMonitor: FocusMonitor,
		@Inject(MAT_TOOLTIP_SCROLL_STRATEGY) scrollStrategy: unknown,
		@Optional() dir: Directionality,
		@Optional() @Inject(MAT_TOOLTIP_DEFAULT_OPTIONS) defaultOptions: MatTooltipDefaultOptions,
		@Inject(DOCUMENT) _document: unknown
	) {
		super(
			overlay,
			elementRef,
			scrollDispatcher,
			viewContainerRef,
			ngZone,
			platform,
			ariaDescriber,
			focusMonitor,
			scrollStrategy,
			dir,
			defaultOptions,
			_document
		);
	}

	/**
	 * update tooltip
	 */
	@HostListener('mouseover')
	public showTooltip(): void {
		this.message = this.tooltipText;

		if (this.shTooltipDisabled) {
			this.disabled = this.shTooltipDisabled;
		} else if (this.shTooltipElement) {
			this.disabled = !this.doesTextOverflow(this.shTooltipElement);
		}

		if (this.shTooltipPosition) {
			this.position = this.shTooltipPosition;
		}
	}

	private doesTextOverflow(e: HTMLElement | MatButton | MatListItem | MatOption): boolean {
		if (e instanceof MatButton) return this.doesMatButtonTextOverflow(e);
		if (e instanceof MatListItem) return this.doesListItemTextOverflow(e);
		if (e instanceof MatOption) return this.doesOptionItemTextOverflow(e);
		return e ? (!this.vertical ? e.offsetWidth < e.scrollWidth : e.offsetHeight < e.scrollHeight) : false;
	}

	private doesMatButtonTextOverflow(e: MatButton): boolean {
		const buttonLabel = e._elementRef?.nativeElement?.querySelector('.mdc-button__label');
		return buttonLabel
			? !this.vertical
				? buttonLabel.offsetWidth < buttonLabel.scrollWidth
				: buttonLabel.offsetHeight < buttonLabel.scrollHeight
			: false;
	}

	private doesListItemTextOverflow(e: MatListItem): boolean {
		const listItem = e._elementRef?.nativeElement?.querySelector<HTMLElement>('.mdc-list-item__content');
		return listItem
			? !this.vertical
				? listItem.offsetWidth < listItem.scrollWidth
				: listItem.offsetHeight < listItem.scrollHeight
			: false;
	}

	private doesOptionItemTextOverflow(e: MatOption): boolean {
		const optionItem = e._getHostElement()?.querySelector<HTMLElement>('.mdc-list-item__primary-text');
		return optionItem
			? !this.vertical
				? optionItem.offsetWidth < optionItem.scrollWidth
				: optionItem.offsetHeight < optionItem.scrollHeight
			: false;
	}
}
